import { useEffect, useMemo, useState } from "react";
import { Building2, Download, Globe2, Mail, MapPin, Phone, Plus } from "lucide-react";
import { qrDataUrl } from "@/lib/vcardQr";

const labels = {
  presentation: "À propos",
  specialties: "Domaines d’intervention",
  services: "Accompagnements",
  socials: "Me retrouver",
};

export default function VCardPublicCard({ card, vcfUrl, qrUrl, qrLabel }) {
  const [qr, setQr] = useState("");
  const fields = useMemo(
    () => [...(card.standard_fields || [])].filter((item) => item.visible).sort((a, b) => a.order - b.order),
    [card.standard_fields]
  );
  const visible = useMemo(() => new Set(fields.map((item) => item.key)), [fields]);

  useEffect(() => {
    const destination = qrUrl || vcfUrl;
    if (destination) qrDataUrl(destination, 420).then(setQr).catch(() => setQr(""));
    else setQr("");
  }, [qrUrl, vcfUrl]);

  const shown = (key) => visible.has(key);
  const identity = [card.first_name, card.last_name].filter(Boolean).join(" ");
  const address = [card.street, [card.postal_code, card.city].filter(Boolean).join(" "), card.country].filter(Boolean).join(", ");
  const monogram = [card.first_name, card.last_name]
    .filter(Boolean)
    .map((value) => value[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const actions = [
    shown("phones") && card.phone
      ? { label: "Appeler", href: `tel:${card.phone.replace(/\s/g, "")}`, icon: Phone }
      : null,
    shown("email") && card.email
      ? { label: "Écrire", href: `mailto:${card.email}`, icon: Mail }
      : null,
    shown("website") && card.website
      ? { label: "Site web", href: card.website, icon: Globe2, external: true }
      : null,
  ].filter(Boolean);

  const contactRows = [
    shown("phones") && card.phone
      ? { label: "Tél.", value: card.phone, href: `tel:${card.phone.replace(/\s/g, "")}` }
      : null,
    shown("phones") && card.phone_secondary
      ? { label: "Tél. 2", value: card.phone_secondary, href: `tel:${card.phone_secondary.replace(/\s/g, "")}` }
      : null,
    shown("email") && card.email
      ? { label: "E-mail", value: card.email, href: `mailto:${card.email}` }
      : null,
    shown("website") && card.website
      ? { label: "Web", value: card.website.replace(/^https?:\/\//, ""), href: card.website, external: true }
      : null,
  ].filter(Boolean);

  return (
    <main className="mx-auto min-h-full w-full max-w-[430px] overflow-hidden bg-bone-light font-manrope text-charcoal shadow-2xl">
      <header className="relative min-h-[395px] overflow-hidden bg-charcoal text-bone-light">
        {shown("photo") && card.photo_url ? (
          <div
            className="absolute inset-y-0 right-0 w-[62%] overflow-hidden"
            style={{ clipPath: "polygon(18% 0, 100% 0, 100% 100%, 34% 100%)" }}
          >
            <img src={card.photo_url} alt={identity || "Portrait"} className="h-full w-full object-cover object-center" />
            <div className="absolute inset-0 bg-gradient-to-r from-charcoal/40 via-transparent to-transparent" />
          </div>
        ) : (
          <div className="absolute -right-16 top-8 h-72 w-72 rounded-full border border-sage/20 bg-bone-light/5" />
        )}
        <div
          className="absolute inset-y-0 left-[61%] z-10 w-[6px] origin-top -rotate-[7deg] bg-sage shadow-lg"
          aria-hidden="true"
        />

        <div className="relative z-20 flex min-h-[395px] flex-col px-6 pb-7 pt-6">
          <div className="min-h-[92px]">
            {shown("logo") && card.logo_url ? (
              <img
                src={card.logo_url}
                alt={`Logo ${card.company || ""}`}
                className="max-h-20 max-w-[145px] object-contain object-left"
              />
            ) : (
              <div className="font-playfair text-6xl leading-none tracking-[-.08em] text-bone-light">{monogram || "E"}</div>
            )}
            {shown("company") && card.company && (
              <p className="mt-2 max-w-[180px] text-[8px] font-semibold uppercase tracking-[.2em] text-bone-light/65">
                {card.company}
              </p>
            )}
          </div>

          <div className="mt-auto max-w-[94%]">
            {shown("specialties") && card.specialties?.length > 0 && (
              <p className="mb-2 max-w-[235px] text-[14px] font-extrabold uppercase leading-[1.35] tracking-[.16em] text-bone-light">
                {card.specialties.slice(0, 3).join(" · ")}
              </p>
            )}
            {shown("identity") && identity && (
              <h1 className="font-playfair text-[34px] leading-[1.02] text-bone-light drop-shadow-sm">
                {card.first_name} <span className="text-sage-light">{card.last_name}</span>
              </h1>
            )}
            {shown("job_title") && card.job_title && (
              <p className="mt-2 max-w-[270px] text-[13px] font-medium leading-[1.45] text-bone-light/95">
                {card.job_title}
              </p>
            )}
          </div>
        </div>
      </header>

      <div className="space-y-5 bg-white px-[22px] py-[22px]">
        {shown("quote") && card.quote && (
          <blockquote className="border-l-[3px] border-sage py-0.5 pl-4 font-playfair text-[16px] italic leading-[1.45] text-charcoal">
            « {card.quote} »
          </blockquote>
        )}

        <a
          href={vcfUrl || undefined}
          download
          aria-disabled={!vcfUrl}
          className={`btn-primary flex min-h-[56px] w-full items-center justify-center gap-3 !rounded-2xl text-[16px] ${!vcfUrl ? "pointer-events-none opacity-50" : ""}`}
        >
          <Plus size={18} strokeWidth={2.5} /> Ajouter à mes contacts
        </a>

        {actions.length > 0 && (
          <nav className="grid grid-cols-3 gap-2.5" aria-label="Actions de contact">
            {actions.map(({ label, href, icon: Icon, external }) => (
              <a
                key={label}
                href={href}
                target={external ? "_blank" : undefined}
                rel={external ? "noreferrer" : undefined}
                className="flex min-h-[70px] flex-col items-center justify-center gap-2 rounded-xl border border-sage/15 bg-white text-[11px] font-extrabold text-charcoal shadow-sm transition hover:border-sage/60 hover:bg-sage/5 focus:outline-none focus:ring-2 focus:ring-sage"
              >
                <Icon size={18} className="text-sage" />
                {label}
              </a>
            ))}
          </nav>
        )}

        {shown("presentation") && card.presentation && (
          <section>
            <h2 className="mb-2 text-[10px] font-extrabold uppercase tracking-[.2em] text-sage">{labels.presentation}</h2>
            <p className="whitespace-pre-line text-[13px] leading-6 text-charcoal-muted">{card.presentation}</p>
          </section>
        )}

        {shown("specialties") && card.specialties?.length > 0 && (
          <section>
            <h2 className="mb-3 text-[10px] font-extrabold uppercase tracking-[.2em] text-sage">{labels.specialties}</h2>
            <div className="grid grid-cols-2 gap-2">
              {card.specialties.map((item) => (
                <div
                  key={item}
                  className="flex min-h-[50px] items-center rounded-xl border-l-[3px] border-sage bg-bone-light px-3 py-2 text-[11px] font-extrabold leading-tight text-charcoal"
                >
                  {item}
                </div>
              ))}
            </div>
          </section>
        )}

        {shown("services") && card.services?.length > 0 && (
          <section>
            <h2 className="mb-3 text-[10px] font-extrabold uppercase tracking-[.2em] text-sage">{labels.services}</h2>
            <div className="flex flex-wrap gap-2">
              {card.services.map((item) => (
                <span key={item} className="rounded-full border border-sage/15 bg-bone-light px-3 py-2 text-[11px] font-bold">
                  {item}
                </span>
              ))}
            </div>
          </section>
        )}

        {contactRows.length > 0 && (
          <dl className="border-t border-sage/15">
            {contactRows.map((row) => (
              <div
                key={`${row.label}-${row.value}`}
                className="grid grid-cols-[76px_1fr] items-center border-b border-sage/15 py-3 text-[11px]"
              >
                <dt className="text-[8px] font-extrabold uppercase tracking-[.15em] text-charcoal-soft">{row.label}</dt>
                <dd className="min-w-0">
                  <a
                    href={row.href}
                    target={row.external ? "_blank" : undefined}
                    rel={row.external ? "noreferrer" : undefined}
                    className="block truncate font-semibold text-charcoal hover:text-sage"
                  >
                    {row.value}
                  </a>
                </dd>
              </div>
            ))}
          </dl>
        )}

        {shown("address") && address && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-start gap-3 rounded-xl bg-bone-light p-4 text-[12px] leading-5 text-charcoal-muted"
          >
            <MapPin size={17} className="mt-0.5 shrink-0 text-sage" />
            {address}
          </a>
        )}

        {shown("company") && card.company && (
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[.14em] text-charcoal-soft">
            <Building2 size={14} className="text-sage" />
            {card.company}
          </div>
        )}

        {shown("socials") && card.socials?.some((item) => item.visible) && (
          <section>
            <h2 className="mb-3 text-[10px] font-extrabold uppercase tracking-[.2em] text-sage">{labels.socials}</h2>
            <div className="flex flex-wrap gap-2">
              {card.socials.filter((item) => item.visible).map((item) => (
                <a
                  key={`${item.label}-${item.url}`}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full border border-sage/20 px-4 py-2 text-[11px] font-bold hover:border-sage"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </section>
        )}

        {(card.custom_fields || [])
          .filter((item) => item.visible)
          .sort((a, b) => a.order - b.order)
          .map((item, index) => (
            <section key={`${item.title}-${index}`} className="rounded-xl border-l-[3px] border-sage bg-bone-light p-4">
              <h2 className="text-[12px] font-extrabold text-charcoal">
                {item.icon && <span className="mr-2" aria-hidden="true">{item.icon}</span>}
                {item.title}
              </h2>
              {item.content && <p className="mt-2 whitespace-pre-line text-[12px] leading-5 text-charcoal-muted">{item.content}</p>}
              {item.link && (
                <a href={item.link} target="_blank" rel="noreferrer" className="mt-3 inline-block text-[11px] font-extrabold text-sage underline">
                  En savoir plus
                </a>
              )}
            </section>
          ))}

        {qr && (
          <div className="border-t border-sage/15 pt-5 text-center">
            <img src={qr} alt="QR code de téléchargement direct du contact" className="mx-auto h-36 w-36" />
            <p className="mt-2 text-[10px] text-charcoal-muted">{qrLabel || "Scannez pour ajouter directement le contact"}</p>
          </div>
        )}

        <div className="flex items-center justify-center gap-2 pt-1 text-[9px] font-bold uppercase tracking-[.18em] text-charcoal-soft">
          <Download size={12} /> Carte de visite numérique
        </div>
      </div>
    </main>
  );
}
